import * as Sentry from '@sentry/react'
import { BUILD_VERSION, BUILD_SHA } from './buildInfo'

// SPEC-SENTRY-VERSION-AND-USER-V1 (D-461) ITEM_1: release を「版番号+短縮SHA」に組み立てる純関数。
//   例 buildSentryRelease('1.0.4','40bc464') = '1.0.4+40bc464'。version.json / 版パネルと同一ソース
//   (buildInfo の BUILD_VERSION / BUILD_SHA)に寄せ、Sentry の release と表示の突合を可能にする
//   (VITE_VERCEL_GIT_COMMIT_SHA の40桁系統は使わない=屍一への防御)。欠落時も 'dev' / 'local' に
//   フォールバックし、'undefined' / 'null' 混じりの文字列を返さない。
export function buildSentryRelease(version, sha) {
  const v = version || 'dev'
  const s = sha || 'local'
  return `${v}+${s}`
}

function scrubEvent(event) {
  try {
    const str = JSON.stringify(event)
    const cleaned = str
      .replace(/(token%3D|token=)[^"&\s]+/g, '$1[REDACTED]')
      .replace(/"[^"]{3,}@[^"]+\.[^"]{2,}"/g, '"[email]"')
      .replace(/\b0[789]\d{8,9}\b/g, '[phone]')
    return JSON.parse(cleaned)
  } catch {
    return event
  }
}

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!import.meta.env.PROD || !dsn) {
    console.info('[sentry] skipped (not prod or no DSN)')
    return
  }

  Sentry.init({
    dsn,
    environment: typeof __VERCEL_ENV__ !== 'undefined' ? __VERCEL_ENV__ : 'production',
    // D-461 ITEM_1: release = 版番号+短縮SHA(buildInfo と同一ソース)。VITE_VERCEL_GIT_COMMIT_SHA は参照しない。
    release:     buildSentryRelease(BUILD_VERSION, BUILD_SHA),
    enabled:     true,
    tracesSampleRate:         1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    beforeSend(event) {
      return scrubEvent(event)
    },
  })

  // D-461 追記(GATE_3): ログの release 表示も実際に渡す値(buildSentryRelease)に揃える。
  //   VITE_VERCEL_GIT_COMMIT_SHA を残すと preview/development でログと実 release が食い違うため撤去。
  console.info('[sentry] initialized, release=' + buildSentryRelease(BUILD_VERSION, BUILD_SHA))
}

export function setSentryContext({ organizationId, storeCode, page, sessionId } = {}) {
  Sentry.setTags({
    organization_id: organizationId ?? 'unknown',
    store_code:      storeCode      ?? 'unknown',
    page:            page           ?? 'unknown',
    session_id:      sessionId      ?? 'unknown',
  })
}

// SPEC-SENTRY-VERSION-AND-USER-V1 (D-461) ITEM_2: ログイン成功時に Sentry.setUser を呼ぶ。
//   渡すのは id(=staff_id)のみ。name / email / 電話 / PIN に類する値は最初から一切入れない
//   (scrubEvent の事後除去に頼らない)。認証層は本関数経由で呼び、Sentry を直接 import しない。
export function setSentryUser(staffId) {
  Sentry.setUser({ id: staffId })
}

// ログアウト時に前の利用者を後続イベントから外す(共有端末運用)。明示ログアウト/アイドルの両経路で呼ぶ。
export function clearSentryUser() {
  Sentry.setUser(null)
}

export { Sentry }
